import type { LensCard, LensCardLayout } from '~/types/leLens';

import { LENS_FRAME_GAP_PX, LENS_FRAME_ROW_PX, frameRowSpan } from './cardGrid';

/** Bound the reading surface; longer results scroll inside the card. */
export const TABLE_AUTO_HEIGHT_MAX_PX = 560;
export const TABLE_COLLAPSED_ROW_SPAN = frameRowSpan(52);

export function tableUsesAutoHeight(card: LensCard): boolean {
  return card.type === 'table' && card.layout?.by !== 'user';
}

/** Measure the intrinsic table, not its scroll container or assigned frame.
 * Subtracting the body leaves the real header, footer, reading and padding.
 * None of those measurements depends on the table's assigned height. */
export function measureTableHeight(frame: HTMLElement): number | undefined {
  const content = frame.querySelector<HTMLElement>('[data-lens-table-content]');
  const shell = content?.closest<HTMLElement>('.MuiCard-root');
  const body = content?.closest<HTMLElement>('[data-lens-card-body]');
  if (!content || !shell || !body || shell.getBoundingClientRect().width <= 0) return undefined;
  const container = content.closest<HTMLElement>('[data-lens-table-container]');
  const notice = body.querySelector<HTMLElement>('[data-lens-table-notice]');
  const noticeHeight = notice ? notice.getBoundingClientRect().height + 4 : 0;
  const scrollbar = container ? container.offsetHeight - container.clientHeight : 0;
  const chrome = shell.getBoundingClientRect().height - body.getBoundingClientRect().height;
  const height = chrome + content.getBoundingClientRect().height + noticeHeight + scrollbar;
  return height > 0 ? Math.min(TABLE_AUTO_HEIGHT_MAX_PX, Math.ceil(height)) : undefined;
}

/** Reading never writes geometry. A gesture saves the displayed frames as
 * starting estimates, including cards moved by compaction, but only an
 * explicit vertical resize makes a table's height fixed. */
export function preserveTableHeightModes(
  cards: ReadonlyArray<LensCard>,
  frames: Record<string, LensCardLayout>,
  heightResizedCardId?: string,
): Record<string, LensCardLayout> {
  const byId = new Map(cards.map((card) => [card.id, card]));
  return Object.fromEntries(
    Object.entries(frames).map(([id, frame]) => {
      const card = byId.get(id);
      if (!card || !tableUsesAutoHeight(card)) return [id, frame];
      if (id === heightResizedCardId) return [id, { ...frame, by: 'user' }];
      return [id, { ...frame, by: card.layout?.by ?? 'auto' }];
    }),
  );
}

export function autoTableRowSpan(height: number): number {
  const maximumRows = Math.floor(
    (TABLE_AUTO_HEIGHT_MAX_PX + LENS_FRAME_GAP_PX) / (LENS_FRAME_ROW_PX + LENS_FRAME_GAP_PX),
  );
  return Math.min(maximumRows, frameRowSpan(height));
}
